import { z } from 'zod';
import { ftpAccountSchema } from '../status/file.js';
import { flowModeSchema } from '../status/run.js';
import { isoDateTimeSchema, objectIdSchema } from './common.js';
/**
 * Réglages généraux (`02` §12 onglet 1, `03` §2 `settings`).
 * Les secrets ne sortent jamais de la base : l'API expose seulement un booléen
 * « configuré » (`04` §6 : jamais de secret dans les logs ni les réponses).
 */
export const generalSettingsSchema = z.object({
    defaultOffsetMinutes: z.number().int().min(1).max(120),
    csvRetentionDays: z.number().int().min(1),
    eventsRetentionDays: z.number().int().min(1),
    pollIntervalMinutes: z.number().int().min(1),
    webhookSilenceHours: z.number().int().min(1),
    noReturnFilesBusinessDays: z.number().int().min(1),
});
export const ftpAccountSettingsSchema = z.object({
    user: z.string(),
    passwordConfigured: z.boolean(),
});
export const ftpSettingsSchema = z.object({
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535),
    dirIn: z.string().min(1),
    dirOut: z.string().min(1),
    secure: z.boolean(),
    account215: ftpAccountSettingsSchema,
    account216: ftpAccountSettingsSchema,
});
export const ftpSettingsInputSchema = z.object({
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535),
    dirIn: z.string().min(1),
    dirOut: z.string().min(1),
    secure: z.boolean().default(false),
    account215: z.object({ user: z.string(), password: z.string().optional() }),
    account216: z.object({ user: z.string(), password: z.string().optional() }),
});
export const pennylaneSettingsSchema = z.object({
    enabled: z.boolean(),
    mode: flowModeSchema,
    apiUrl: z.string().min(1),
    apiKeyConfigured: z.boolean(),
    schedule: z.string().min(1),
    catchupDays: z.number().int().min(1),
    importFlag: z.object({ name: z.string().min(1), value: z.string().min(1) }),
    /**
     * Transporteur des CSV de facture. `null` = bloc transport vide, le
     * comportement de la v1 (`02` §12.4).
     */
    defaultCarrier: z.object({ code: z.string(), label: z.string() }).nullable(),
});
/*
 * Il n'y a plus de schéma d'entrée ici : depuis le 10/09/2026 les réglages
 * Pennylane s'écrivent uniquement par `/api/pennylane/settings`
 * (`pennylaneSettingsUpdateBodySchema`, `dto/pennylane.ts`). Le schéma de
 * lecture reste, il compose l'agrégat `settingsSchema`.
 */
export const fulfillmentSettingsSchema = z.object({
    mode: flowModeSchema,
    schedule: z.string().min(1),
    defaultReturnCompany: z.string().min(1),
});
export const alertSettingsSchema = z.object({
    emails: z.array(z.email()),
    slackEnabled: z.boolean(),
    /** Le webhook Slack lui-même est une variable d'environnement (`03` §4.1). */
    slackConfigured: z.boolean(),
});
export const alertSettingsInputSchema = alertSettingsSchema.omit({ slackConfigured: true });
export const maintenanceHistoryEntrySchema = z.object({
    at: isoDateTimeSchema,
    enabled: z.boolean(),
    reason: z.string().nullable(),
    by: objectIdSchema.nullable(),
    byName: z.string().nullable(),
});
export const maintenanceStateSchema = z.object({
    enabled: z.boolean(),
    since: isoDateTimeSchema.nullable(),
    reason: z.string().nullable(),
    by: objectIdSchema.nullable(),
    byName: z.string().nullable(),
    queuedCount: z.number().int().min(0),
    history: z.array(maintenanceHistoryEntrySchema),
});
export const maintenanceToggleBodySchema = z.object({
    enabled: z.boolean(),
    reason: z.string().min(1).optional(),
    /** À la désactivation : envoyer immédiatement les fichiers en file (`01` §8). */
    flushQueue: z.boolean().default(false),
});
export const settingsSchema = z.object({
    general: generalSettingsSchema,
    ftp: ftpSettingsSchema,
    pennylane: pennylaneSettingsSchema,
    fulfillment: fulfillmentSettingsSchema,
    alerts: alertSettingsSchema,
    maintenance: maintenanceStateSchema,
});
/** Résultat d'un bouton « Tester » (FTP, Pennylane, email, Slack). */
export const connectionTestResultSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
    detail: z.string().optional(),
    durationMs: z.number().int().min(0),
});
export const ftpTestBodySchema = z.object({
    account: ftpAccountSchema,
});
/** Réponse de `GET /health` (`01` §10, `04` §3.1). */
export const healthResponseSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    mongo: z.boolean(),
    worker: z.object({
        lastHeartbeatAt: isoDateTimeSchema.nullable(),
        stale: z.boolean(),
    }),
    version: z.string(),
});
//# sourceMappingURL=settings.js.map